import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { supabase } from "../lib/supabase";
import type { AssessmentResult } from "../types/results";
import type { Score } from "../types/index";
import type { HarmonicScore } from "../types/harmonic";
import { displayErrorMessage } from "../utils/functions";

export interface ResultsContextType {
  saveResults: (results: Omit<AssessmentResult, "id">) => Promise<boolean>;
  getResults: (userId: string, requestingUserId?: string) => Promise<AssessmentResult | null>;
  isLoading: boolean;
  error: string | null;
}

const ResultsContext = createContext<ResultsContextType | undefined>(undefined);
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export function ResultsProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resultsCache = useRef<Map<string, AssessmentResult>>(new Map());

  const getResults = useCallback(
    async (
      userId: string,
      requestingUserId?: string,
      retryCount = 0
    ): Promise<AssessmentResult | null> => {
      try {
        if (resultsCache.current.has(userId)) {
          return resultsCache.current.get(userId) || null;
        }

        setIsLoading(true);
        setError(null);

        if (requestingUserId && requestingUserId !== userId) {
          const { data: respondent, error: respondentError } = await supabase
            .from("respondents")
            .select("id")
            .eq("user_id", userId)
            .eq("coach_id", requestingUserId)
            .maybeSingle();

          if (respondentError || !respondent) {
            throw new Error("Unauthorized access to respondent data");
          }
        }

        const { data: resultData, error: resultError } = await supabase
          .from("results")
          .select("*")
          .eq("user_id", userId)
          .order("completed_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (resultError) {
          throw resultError;
        }

        if (!resultData) {
          return null;
        }

        const result: AssessmentResult = {
          id: resultData.id,
          userId: resultData.user_id,
          coachId: resultData.coach_id || undefined,
          partnerId: resultData.partner_id || undefined,
          scores: resultData.scores as Score[],
          harmonicScores: resultData.harmonic_scores as HarmonicScore[],
          completedAt: new Date(resultData.completed_at || resultData.created_at || Date.now()),
          aiAnalysis: resultData.ai_analysis || undefined,
          version: resultData.version || undefined,
          originalResultId: resultData.original_result_id || undefined,
        };

        resultsCache.current.set(userId, result);

        return result;
      } catch (err: any) {
        console.error("Error getting results:", err);

        if (retryCount < MAX_RETRIES) {
          await new Promise((resolve) =>
            setTimeout(resolve, RETRY_DELAY * (retryCount + 1))
          );
          return getResults(userId, requestingUserId, retryCount + 1);
        }

        setError(displayErrorMessage(err, "Failed to load assessment results"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const saveResults = useCallback(
    async (results: Omit<AssessmentResult, "id">) => {
      try {
        setIsLoading(true);
        setError(null);

        const { data: respondent, error: respondentError } = await supabase
          .from("respondents")
          .select("id, coach_id, total_assessments")
          .eq("user_id", results.userId)
          .maybeSingle();

        if (respondentError) throw respondentError;
        if (!respondent) throw new Error("No respondent record found");

        const coachId = respondent.coach_id || null;
        const totalAssessments = respondent.total_assessments || 0;
        const isRetake = totalAssessments > 0;

        let originalResultId: string | null = null;
        if (isRetake) {
          const { data: originalResult } = await supabase
            .from("results")
            .select("id")
            .eq("user_id", results.userId)
            .order("completed_at", { ascending: true })
            .limit(1)
            .maybeSingle();

          if (originalResult) {
            originalResultId = originalResult.id;
          }
        }

        const version = isRetake ? totalAssessments + 1 : 1;

        const { data: insertedResult, error: insertError } = await supabase
          .from("results")
          .insert({
            user_id: results.userId,
            coach_id: coachId,
            partner_id: results.partnerId || null,
            scores: results.scores as any,
            harmonic_scores: results.harmonicScores as any,
            ai_analysis: results.aiAnalysis || null,
            version,
            original_result_id: originalResultId,
            completed_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (insertError) throw insertError;

        const nextRetakeDate = new Date();
        nextRetakeDate.setDate(nextRetakeDate.getDate() + 7);

        const { error: updateError } = await supabase
          .from("respondents")
          .update({
            last_assessment_date: new Date().toISOString(),
            total_assessments: totalAssessments + 1,
            next_retake_date: nextRetakeDate.toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", respondent.id);

        if (updateError) {
          console.error("Failed to update respondent record:", updateError);
        }

        resultsCache.current.set(results.userId, {
          id: insertedResult.id,
          ...results,
          coachId: coachId || undefined,
          version,
          originalResultId: originalResultId || undefined,
          completedAt: new Date(),
        });

        return true;
      } catch (err: any) {
        console.error("Error saving results:", err);
        setError("Failed to save assessment results");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return (
    <ResultsContext.Provider
      value={{
        saveResults,
        getResults,
        isLoading,
        error,
      }}
    >
      {children}
    </ResultsContext.Provider>
  );
}

export function useResults() {
  const context = useContext(ResultsContext);
  if (context === undefined) {
    throw new Error("useResults must be used within a ResultsProvider");
  }
  return context;
}
